import { useMemo, useState } from "react";
import { matchesText } from "../../core/textMatch";
import { useTranslation } from "react-i18next";
import { Pin } from "lucide-react";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import type { FilterAst } from "../../core/filter";
import { applyQuickAdd, listPropDefs, quickAddPropDefs } from "../../core/ops";
import { parseQuickAdd } from "../../core/quickadd";
import { relatesOf } from "../../core/structure";
import type { Task } from "../../core/schema";
import { Input } from "../../ui";
import { FILTER_SCHEMA, visibleTasks } from "../shared";

export interface NotebookConfig {
  filter?: FilterAst;
  groupBy?: "tag" | "month" | "none";
  pinnedFirst?: boolean;
  search?: boolean;
}

/** The notebook (spec §9.7 `notebook`, §9.9 `note`): search box, tag facets, cards with first line,
 *  tags and related tasks. Capture with the field on top (a leading ">" is implied). */
export function NotebookView({ config, ctx }: ViewProps<NotebookConfig>) {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [facet, setFacet] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const data = useDocSelector(
    ctx.handle,
    (doc) => {
      const filter = config.filter ?? { type: "note" };
      const v = visibleTasks(doc, filter, Date.now(), {
        includeNotes: true,
        includeDone: true,
        includeSomeday: true,
      });
      const defs = listPropDefs(doc).filter((d) => d.kind === "tag" && !d.builtin);
      return {
        notes: v.tasks,
        tagDefs: defs.map((d) => ({ id: d.id, name: d.name })),
        related: Object.fromEntries(
          v.tasks.map((n) => [
            n.id,
            relatesOf(v.snapshot, n.id).map((id) =>
              String((v.snapshot.tasks[id] as { title?: string })?.title ?? ""),
            ),
          ]),
        ),
      };
    },
    [config],
  );

  const groups = useMemo(() => {
    if (!data) return [] as [string, Task[]][];
    let notes = data.notes.filter(
      (x) => matchesText([x.title, x.notes], q) && (!facet || x.props[facet] === true),
    );
    notes = notes.slice().sort((a, b) => {
      if (config.pinnedFirst !== false) {
        const pa = a.props.pinned === true ? 0 : 1;
        const pb = b.props.pinned === true ? 0 : 1;
        if (pa !== pb) return pa - pb;
      }
      return b.createdAt.localeCompare(a.createdAt);
    });
    const by = config.groupBy ?? "none";
    if (by === "none") return [["", notes]] as [string, Task[]][];
    const map = new Map<string, Task[]>();
    for (const note of notes) {
      const keys =
        by === "month"
          ? [new Date(note.createdAt).toLocaleDateString(i18n.language, { month: "long", year: "numeric" })]
          : data.tagDefs.filter((d) => note.props[d.id] === true).map((d) => `#${d.name}`);
      for (const k of keys.length ? keys : [t("notebook.untagged")])
        map.set(k, [...(map.get(k) ?? []), note]);
    }
    return [...map.entries()];
  }, [data, q, facet, config.groupBy, config.pinnedFirst, i18n.language, t]);

  return (
    <div className="flex h-full flex-col gap-2" data-testid="notebook-view">
      {!ctx.readOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.trim()) return;
            ctx.write((doc) =>
              applyQuickAdd(
                doc,
                parseQuickAdd(`> ${draft}`, { propDefs: quickAddPropDefs(doc), now: new Date() }),
              ),
            );
            setDraft("");
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("notebook.capture")}
            aria-label={t("notebook.capture")}
            data-testid="notebook-capture"
          />
        </form>
      )}
      {config.search !== false && (
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("notebook.search")}
          aria-label={t("notebook.search")}
          className="h-[30px] py-0 text-[13px]"
        />
      )}
      {data && data.tagDefs.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("notebook.tags")}>
          {data.tagDefs.map((d) => (
            <button
              key={d.id}
              type="button"
              aria-pressed={facet === d.id}
              onClick={() => setFacet(facet === d.id ? null : d.id)}
              className="h-[24px] rounded-sm border border-neutral-200 px-2 text-[12px] text-neutral-600 aria-pressed:border-accent aria-pressed:bg-accent-tint aria-pressed:text-accent dark:border-neutral-800 dark:text-neutral-300"
            >
              #{d.name}
            </button>
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {groups.every(([, ns]) => ns.length === 0) && (
          <p className="px-1 py-4 text-[12.5px] text-neutral-500">{t("notebook.empty")}</p>
        )}
        {groups.map(([label, notes]) => (
          <section key={label} className="mb-3">
            {label && (
              <h3 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
                {label}
              </h3>
            )}
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2">
              {notes.map((n) => (
                <li key={n.id} data-testid="note-card">
                  <button
                    type="button"
                    onClick={() => ctx.openTask(n.id)}
                    className="flex h-full w-full flex-col gap-1 rounded-md border border-neutral-200 bg-[var(--surface)] p-3 text-left shadow-[var(--shadow-sm)] hover:border-neutral-700 dark:border-neutral-800"
                  >
                    <span className="flex items-start gap-1.5 text-sm font-medium">
                      {n.props.pinned === true && (
                        <Pin
                          size={13}
                          aria-label={t("props.pinned")}
                          className="mt-0.5 shrink-0 text-accent"
                        />
                      )}
                      <span className="line-clamp-2">
                        {(n.notes || n.title).split("\n")[0] || t("task.untitledNote")}
                      </span>
                    </span>
                    <span className={clsx("line-clamp-3 text-[12.5px] text-neutral-500")}>
                      {n.notes.split("\n").slice(1).join(" ")}
                    </span>
                    <span className="mt-auto flex flex-wrap gap-1 pt-1 text-[11px] text-neutral-500">
                      {data?.tagDefs
                        .filter((d) => n.props[d.id] === true)
                        .map((d) => (
                          <span key={d.id}>#{d.name}</span>
                        ))}
                      {(data?.related[n.id] ?? []).map((title, i) => (
                        <span key={i} className="text-accent">
                          ↔ {title}
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

registerViewType<NotebookConfig>({
  id: "notebook",
  label: "views.notebook.label",
  icon: "notebook",
  level: "standard",
  defaultConfig: { groupBy: "none", pinnedFirst: true, search: true },
  configSchema: {
    type: "object",
    properties: {
      groupBy: { type: "string", title: "config.groupBy", enum: ["none", "tag", "month"] },
      pinnedFirst: { type: "boolean", title: "config.pinnedFirst" },
      search: { type: "boolean", title: "config.search" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: NotebookView,
  minSize: { w: 4, h: 4 },
});
